import { useContext, useEffect, useState } from 'react';
import {
  GameStateContext,
  LinkRowRefContext,
} from '../../Providers/GameStates/GameState.jsx';
import { Box, styled } from '@mui/material';
import './Keyboard.css'
import { Storage } from '../../utils.jsx';

const KeyboardStyle = styled(Box)(({ theme }) => ({
  backgroundColor: theme.custom.surface,
  border: `1px solid ${theme.custom.border}`,
  boxShadow: '0 20px 60px rgba(15, 23, 42, 0.20)',
  backdropFilter: 'blur(22px)',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  bottom: 0,
  left: 0,
  zIndex: 900,
  padding: '0.65rem',
  maxWidth: '680px',
  width: 'calc(100% - 1.25rem)',
  position: 'fixed',
  right: 0,
  margin: 'auto auto 10px',
  borderRadius: '22px',
}));

const KeyboardButtonStyle = styled('button')(({ theme }) => ({
  backgroundColor: theme.custom.kbBtnBg,
  color: theme.custom.kbColor,
  fontSize: '1.25rem',
  fontWeight: '700',
  padding: '0.45rem',
  cursor: 'pointer',
  textTransform: 'uppercase',
  display: 'inline-block',
  borderRadius: '10px',
  transition: 'transform 100ms ease, background-color 180ms ease, box-shadow 180ms ease',
  border: `1px solid ${theme.custom.border}`,
  flex: '1',
  margin: '0 0.25rem',
  minHeight: '52px',
  boxShadow: '0 3px 0 rgba(15, 23, 42, 0.16)',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(2px)',
    boxShadow: 'none',
  },
  '@media (max-width: 768px)': {
    fontSize: '1rem',
    padding: '0.2rem',
    margin: '0 0.15rem',
    minHeight: '46px',
  },
}));

const ActionButton = styled(KeyboardButtonStyle)(({ theme }) => ({
  flex: '1.5',
  fontSize: '0.85rem',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const FourthRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

function Keyboard() {
  const { setLastKeyPressed } = useContext(GameStateContext);
  const { ref: linkRowRef, centerLinkDiv } = useContext(LinkRowRefContext);
  const [buttonLabels, setButtonLabels] = useState({
    enter: '⏎',
    delete: '⌫',
  });
  const [swapKeyboardActions, setSwapKeyboardActions] = useState(() =>
    Storage.getFromLocalStorage('swapKeyboardActions', false) === true,
  );

  function handleKeyDown(e) {
    const target = e.target;
    const isEditableTarget =
      target instanceof HTMLElement &&
      (target.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

    if (isEditableTarget) {
      return;
    }

    setLastKeyPressed(e.key);
    centerLinkDiv(linkRowRef);
  }

  function handleKeyboardClick(e) {
    const target = e.target;

    if (!target.classList.contains('keyboard__button')) {
      return;
    }

    let key = target.value.trim();

    if (key === 'Delete') {
      key = 'Backspace';
    }

    setLastKeyPressed(key);
    centerLinkDiv(linkRowRef);
  }

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleLayoutChange = (event) => {
      setSwapKeyboardActions(event.detail.swapKeyboardActions);
    };
    window.addEventListener('keyboard-layout-change', handleLayoutChange);
    return () => window.removeEventListener('keyboard-layout-change', handleLayoutChange);
  }, []);

  const leftAction = swapKeyboardActions
    ? { value: 'Delete', desktopLabel: buttonLabels.delete, mobileLabel: 'Backspace' }
    : { value: 'Enter', desktopLabel: buttonLabels.enter, mobileLabel: 'Enter' };
  const rightAction = swapKeyboardActions
    ? { value: 'Enter', desktopLabel: buttonLabels.enter, mobileLabel: 'Enter' }
    : { value: 'Delete', desktopLabel: buttonLabels.delete, mobileLabel: 'Backspace' };

  return (
    <div onClick={handleKeyboardClick}>
      <Box
        id="keyboard-wrapper"
        role="region"
        aria-labelledby="keyboard-label"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          position: 'fixed',
          left: 0,
          bottom: 0,
          width: '100%',
          minHeight: '210px',
          pointerEvents: 'none',
          pb: 'env(safe-area-inset-bottom)',
        }}
      >
        <h2 id="keyboard-label" className="visually-hidden">
          Onscreen Keyboard
        </h2>
        <KeyboardStyle id="keyboard-cont" sx={{ pointerEvents: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
            <KeyboardButtonStyle className="keyboard__button" value="q">
              q
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="w">
              w
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="e">
              e
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="r">
              r
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="t">
              t
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="y">
              y
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="u">
              u
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="i">
              i
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="o">
              o
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="p">
              p
            </KeyboardButtonStyle>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: '0 3%' }}>
            <KeyboardButtonStyle className="keyboard__button" value="a">
              a
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="s">
              s
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="d">
              d
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="f">
              f
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="g">
              g
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="h">
              h
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="j">
              j
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="k">
              k
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="l">
              l
            </KeyboardButtonStyle>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', margin: '0.5rem 0' }}>
            <ActionButton className="keyboard__button" value={leftAction.value}>
              {leftAction.desktopLabel}
            </ActionButton>
            <KeyboardButtonStyle className="keyboard__button" value="z">
              z
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="x">
              x
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="c">
              c
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="v">
              v
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="b">
              b
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="n">
              n
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value="m">
              m
            </KeyboardButtonStyle>
            <ActionButton className="keyboard__button" value={rightAction.value}>
              {rightAction.desktopLabel}
            </ActionButton>
          </Box>
          <FourthRow>
            <KeyboardButtonStyle className="keyboard__button" value={leftAction.value}>
              {leftAction.mobileLabel}
            </KeyboardButtonStyle>
            <KeyboardButtonStyle className="keyboard__button" value={rightAction.value}>
              {rightAction.mobileLabel}
            </KeyboardButtonStyle>
          </FourthRow>
        </KeyboardStyle>
      </Box>
    </div>
  );
}

export default Keyboard;
